import React, { useMemo, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Dropdown,
  ListGroup,
} from "react-bootstrap";
import Stepper from "@/QuestionnaireBuilder/Stepper";
import LogicPicker from "@/QuestionnaireBuilder/LogicPicker";
import { useQuestionnaire } from "@/QuestionnaireBuilder/QuestionnaireContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { generateClient } from "@aws-amplify/api";
import { getQuestionnaires } from "@/graphql/queries";

const client = generateClient();

export default function QuestionnaireQuestions() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const { setupMode, duplicateId } = location.state || {};

  const {
    meta,
    questions,
    settings,
    updateMeta,
    setSettings,
    addQuestion,
    updateQuestion,
    removeQuestion,
    createEndPage,
    setQuestions,
  } = useQuestionnaire();

  useEffect(() => {
    if (!id) return;
    if (String(meta.id) === String(id) && questions.length > 0) {
      return; 
    }

    const fetchQuestionnaire = async () => {
      try {
        const res = await client.graphql({
          query: getQuestionnaires,
          variables: {
            filters: { id: { eq: id } },
          },
        });
        const items = res?.data?.getQuestionnaires?.questionnaires;
        if (items && items.length > 0) {
          const loadedData = items[0];
          updateMeta("name", loadedData.name || "");
          updateMeta("purpose", loadedData.description || "");
          updateMeta("id", loadedData.id);
          if (loadedData.questions_json) {
            setQuestions(JSON.parse(loadedData.questions_json));
          }
          if (loadedData.settings_json && setSettings) {
            setSettings(JSON.parse(loadedData.settings_json));
          }
        }
      } catch (e) {
        console.error("Error loading questionnaire data:", e);
      }
    };
    fetchQuestionnaire();
  }, [id]); 

  useEffect(() => {
    if (setupMode === "duplicate" && duplicateId) {
      const fetchAndClone = async () => {
        try {
          console.log("Cloning Questionnaire ID:", duplicateId);

          const res = await client.graphql({
            query: getQuestionnaires,
            variables: {
              filters: { id: { eq: duplicateId } },
            },
          });

          const items = res?.data?.getQuestionnaires?.questionnaires;

          if (items && items.length > 0) {
            const source = items[0];

            updateMeta("name", `Copy of ${source.name}`);
            updateMeta("purpose", source.description || "");

            updateMeta("id", null);

            if (source.questions_json) {
              setQuestions(JSON.parse(source.questions_json));
            }

            if (source.settings_json) {
              setSettings(JSON.parse(source.settings_json));
            }

            window.history.replaceState({}, document.title);
          } else {
            console.warn("No questionnaire found to duplicate");
          }
        } catch (e) {
          console.error("Error duplicating:", e);
        }
      };

      fetchAndClone();
    }
  }, [setupMode, duplicateId]);

  const PURPLE = "#2E2868";
  const pillInputStyle = useMemo(
    () => ({
      fontSize: "0.95rem",
      borderRadius: "50px",
      padding: "0.6rem 1.2rem",
      border: "1px solid #ccc",
      boxShadow: "none",
      width: "100%",
      maxWidth: "460px",
    }),
    [],
  );
  const subLabelStyle = { fontSize: "0.85rem", color: "#6c757d" };
  const labelStyle = { fontWeight: 600, color: PURPLE, fontSize: "0.92rem" };

  const renderAddQuestionBtn = () => (
    <Dropdown>
      <Dropdown.Toggle
        id="add-question-toggle"
        style={{
          backgroundColor: PURPLE,
          borderColor: PURPLE,
          borderRadius: 24,
          padding: "0.5rem 1rem",
          fontWeight: 500,
        }}
      >
        + Add Question
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => addQuestion("multiple")}>
          <div style={{ fontWeight: 600 }}>Multiple choice</div>
          <div style={{ fontSize: 12, color: "#6c757d" }}>
            Add questions and conditional logic to filter leads.
          </div>
        </Dropdown.Item>
        <Dropdown.Item onClick={() => addQuestion("short")}>
          <div style={{ fontWeight: 600 }}>Short answer</div>
          <div style={{ fontSize: 12, color: "#6c757d" }}>
            Get additional info in a sentence or two.
          </div>
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  const goBack = () => {
    if (id) navigate(`/app/customers/questionnaires/${id}/setup`);
    else navigate(`/app/customers/questionnaires/setup`);
  };

  const goNext = () => {
    if (id) navigate(`/app/customers/questionnaires/${id}/review`);
    else navigate(`/app/customers/questionnaires/review`);
  };

  const isNextDisabled =
    !meta.name.trim() || !meta.purpose.trim() || questions.length === 0;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          marginTop: "20px",
        }}
      >
        <Stepper
          labels={["Setup", "Questions", "Review"]}
          activeIndex={1}
          paths={[
            "/app/customers/questionnaires/setup",
            "/app/customers/questionnaires/questions",
            "/app/customers/questionnaires/review",
          ]}
        />
      </div>

      <Container
        fluid
        style={{
          maxWidth: "1100px",
          marginTop: "30px",
          paddingBottom: "300px",
        }}
      >
        <Row className="justify-content-center">
          <Col xs={12}>
            <Card
              style={{
                border: "3px solid #eaeaea",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                width: "100%",
                minHeight: "460px",
                borderRadius: 12,
              }}
            >
              <Card.Body style={{ padding: "2.5rem" }}>
                <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
                  <h5
                    style={{ fontWeight: 600, color: PURPLE, marginBottom: 6 }}
                  >
                    Build your questionnaire
                  </h5>
                  <div style={{ color: "#6c6f82", fontSize: 14 }}>
                    Fill in basic details and add questions to capture the data
                    you need.
                  </div>
                </div>

                <div
                  style={{
                    height: 1,
                    background: "#d7d7df",
                    width: 220,
                    margin: "0 auto 1.25rem auto",
                  }}
                />

                <Row
                  className="align-items-center"
                  style={{ marginBottom: "1.25rem" }}
                >
                  <div style={{ marginLeft: "120px" }}>
                    <div style={{ marginBottom: "1.25rem" }}>
                      <div style={{ ...labelStyle, marginBottom: "0.25rem" }}>
                        Questionnaire Name
                      </div>
                      <Form.Control
                        value={meta.name}
                        onChange={(e) => updateMeta("name", e.target.value)}
                        placeholder="e.g., Customer Onboarding Survey"
                        style={pillInputStyle}
                      />
                    </div>

                    <div style={{ marginBottom: "1.25rem" }}>
                      <div style={{ ...labelStyle, marginBottom: "0.25rem" }}>
                        Questionnaire Purpose
                      </div>
                      <div style={{ ...subLabelStyle, marginBottom: "0.4rem" }}>
                        (e.g., To collect pre-screening information)
                      </div>
                      <Form.Control
                        value={meta.purpose}
                        onChange={(e) => updateMeta("purpose", e.target.value)}
                        placeholder="e.g., To collect pre-screening information"
                        style={pillInputStyle}
                      />
                    </div>
                  </div>
                </Row>

                {questions.length < 1 && (
                  <div
                    style={{ marginBottom: "1.25rem", display: "inline-block" }}
                  >
                    {renderAddQuestionBtn()}
                  </div>
                )}

                <Card
                  style={{
                    border: "1px solid #e7e7ef",
                    background: "#fff",
                    borderBottom: 0,
                  }}
                >
                  <ListGroup variant="flush" className="no-border-list">
                    {questions.map((q, idx) => (
                      <div
                        key={q.id}
                        style={{
                          border: "1px solid #e7e7ef",
                          borderRadius: 12,
                          background: "#fff",
                          padding: 16,
                          marginBottom: 12,
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div style={{ ...labelStyle }}>
                            {q.type === "multiple" && "Multiple choice"}
                            {q.type === "short" && "Short answer"}
                          </div>

                          <div className="d-flex align-items-center gap-2">
                            <Form.Check
                              type="checkbox"
                              label="Optional"
                              checked={!q.required}
                              onChange={(e) =>
                                updateQuestion(q.id, {
                                  required: !e.target.checked,
                                })
                              }
                            />
                            <Button
                              variant="outline-danger"
                              size="sm"
                              title="Delete"
                              onClick={() => removeQuestion(q.id)}
                              style={{
                                borderRadius: 8,
                                padding: "3px 8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </div>
                        </div>

                        <div
                          style={{
                            marginTop: 8,
                            fontWeight: 600,
                            color: "#3f3b6a",
                          }}
                        >
                          Question {idx + 1}
                        </div>
                        <Form.Control
                          placeholder="Example: Do you have diabetes?"
                          value={q.label}
                          onChange={(e) =>
                            updateQuestion(q.id, { label: e.target.value })
                          }
                          style={{ borderRadius: 20, marginTop: 6 }}
                        />

                        {q.type === "multiple" && (
                          <div style={{ marginTop: 14 }}>
                            <div
                              className="row"
                              style={{
                                fontWeight: 600,
                                color: "#3f3b6a",
                                marginBottom: 6,
                              }}
                            >
                              <div className="col-md-6">Answers</div>
                              <div className="col-md-6">Logic</div>
                            </div>

                            {(q.options || []).map((opt, i) => (
                              <div
                                className="row"
                                key={i}
                                style={{ marginBottom: 8 }}
                              >
                                <div className="col-md-6">
                                  <Form.Control
                                    placeholder={`Answer ${i + 1}`}
                                    value={opt.text}
                                    onChange={(e) => {
                                      const next = [...q.options];
                                      next[i] = {
                                        ...next[i],
                                        text: e.target.value,
                                      };
                                      updateQuestion(q.id, { options: next });
                                    }}
                                    style={{ borderRadius: 20 }}
                                  />
                                </div>
                                <div className="col-md-6">
                                  <div className="d-flex align-items-center">
                                    <LogicPicker
                                      value={opt.logic || null}
                                      questions={questions}
                                      currentQuestionId={q.id}
                                      endPages={settings.endPages}
                                      onCreateEndPage={createEndPage}
                                      onChange={(logic) => {
                                        const next = [...q.options];
                                        next[i] = { ...next[i], logic };
                                        updateQuestion(q.id, { options: next });
                                      }}
                                    />
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      style={{
                                        marginLeft: 8,
                                        borderRadius: 6,
                                        padding: "2px 8px",
                                      }}
                                      onClick={() => {
                                        const next = q.options.filter(
                                          (_, j) => j !== i,
                                        );
                                        updateQuestion(q.id, { options: next });
                                      }}
                                      disabled={q.options.length <= 1}
                                    >
                                      −
                                    </Button>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      style={{
                                        marginLeft: 6,
                                        borderRadius: 6,
                                        padding: "2px 8px",
                                      }}
                                      onClick={() => {
                                        const next = [...q.options];
                                        next.splice(i + 1, 0, {
                                          text: "",
                                          logic: null,
                                        });
                                        updateQuestion(q.id, { options: next });
                                      }}
                                    >
                                      +
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {/*  Short Answer UI */}
                        {q.type === "short" && (
                          <div style={{ marginTop: 14 }}>
                            <div
                              className="row"
                              style={{
                                fontWeight: 600,
                                color: "#3f3b6a",
                                marginBottom: 6,
                              }}
                            >
                              <div className="col-md-6">Logic</div>
                            </div>
                            <div className="row">
                              <div className="col-md-6">
                                <LogicPicker
                                  value={q.logic || null}
                                  questions={questions}
                                  currentQuestionId={q.id}
                                  endPages={settings.endPages}
                                  onCreateEndPage={createEndPage}
                                  onChange={(logic) =>
                                    updateQuestion(q.id, { logic })
                                  }
                                />
                              </div>
                              <div className="col-md-6"></div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </ListGroup>
                </Card>

                {questions.length >= 1 && (
                  <div className="d-flex" style={{ marginTop: 12 }}>
                    {renderAddQuestionBtn()}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "2rem",
                  }}
                >
                  <Button
                    variant="outline-secondary"
                    style={{
                      padding: "0.6rem 2rem",
                      borderRadius: 25,
                      borderColor: PURPLE,
                      color: "#6c63ff",
                    }}
                    onClick={goBack}
                  >
                    Back
                  </Button>

                  <Button
                    style={{
                      padding: "0.6rem 2.5rem",
                      borderRadius: 25,
                      backgroundColor: PURPLE,
                      borderColor: "#6c63ff",
                      fontWeight: 500,
                    }}
                    onClick={goNext}
                    disabled={isNextDisabled}
                  >
                    Next
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
